/**
 * Persistência local da notificação de conquistas.
 *
 * Guarda, por usuário, o conjunto de troféus que já foram exibidos como
 * banner — pra não notificar de novo o que o usuário já viu (nem repetir
 * tudo a cada reload). A unidade é `badgeId::cycleKey`: uma badge
 * recorrente ganha em outro ciclo conta como troféu novo.
 */

import type { UserBadge } from '@/services/badges.service'

/** Janela de recência: só notifica conquistas ganhas nos últimos 10 min. */
export const TROPHY_RECENCY_WINDOW_MS = 10 * 60 * 1000

/** Identificador único do troféu (badge + ciclo). */
export function trophyUid(ub: UserBadge): string {
  return `${ub.badge.id}::${ub.cycleKey}`
}

function storageKey(userId: string): string {
  return `trophy-seen:${userId}`
}

/**
 * Lê o seen-set do usuário. Tolera chave ausente ou JSON corrompido —
 * nunca lança (pior caso devolve Set vazio → re-seeda na próxima busca).
 */
export function readSeenSet(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

/**
 * Grava o seen-set. Engole erros de quota / modo privado — perder a
 * persistência é degradação aceitável (no máximo re-notifica depois).
 */
export function writeSeenSet(userId: string, seen: Set<string>): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(Array.from(seen)))
  } catch {
    // quota cheia / localStorage indisponível — segue sem persistir
  }
}

/** `true` se a conquista foi ganha dentro da janela de recência. */
export function isRecent(earnedAt: string, now: number): boolean {
  const ts = Date.parse(earnedAt)
  if (Number.isNaN(ts)) return false
  return now - ts <= TROPHY_RECENCY_WINDOW_MS
}
